import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import swal from 'sweetalert2';

import { ProyectosService } from '../../proyectos.service';
import { Proyecto } from 'src/app/shared/model/gestion-proyectos/proyecto';

interface SubFaseForm {
  nombre: string;
  icon: string;
  completado: boolean;
  opcional?: boolean;
}

@Component({
  selector: 'app-agregar-avance',
  standalone: true,
  templateUrl: './agregar-avance.component.html',
  styleUrls: ['./agregar-avance.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatButtonModule,
    MatIconModule,
  ],
})
export class AgregarAvanceComponent implements OnInit {

  readonly FASE_COLORS = {
    DESARROLLO: { main: '#083e91', light: '#e3eeff' },
    QA:         { main: '#6c58a7', light: '#ede8fa' },
    PRODUCCION: { main: '#212121', light: '#eeeeee' },
  } as const;

  proyecto: Proyecto = new Proyecto();
  fase: string | null = null;
  avanceReal: number | null = null;
  comentario = '';
  qaSubFases: SubFaseForm[] = [];
  prodSubFases: SubFaseForm[] = [];
  isSaving = false;

  constructor(
    public dialogRef: MatDialogRef<AgregarAvanceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Proyecto,
    private proyectosService: ProyectosService,
  ) {}

  ngOnInit(): void {
    this.proyecto   = this.data ?? new Proyecto();
    this.avanceReal = this.proyecto.avanceReal ?? 0;

    const comentarios = this.proyecto.comentariosAvances ?? [];
    const conFase = comentarios.filter(c => c.fase);
    if (conFase.length > 0) {
      const ultimo = conFase[conFase.length - 1];
      this.fase = ultimo.fase!;
      if (ultimo.fase === 'DESARROLLO') {
        this.avanceReal = ultimo.avanceDesarrollo ?? this.proyecto.avanceReal ?? 0;
      } else if (ultimo.fase === 'QA') {
        this.qaSubFases = this.mapSubFasesFromStored(ultimo.qaSubFases, this.getDefaultQaSubFases());
      } else if (ultimo.fase === 'PRODUCCION') {
        this.prodSubFases = this.mapSubFasesFromStored(ultimo.prodSubFases, this.getDefaultProdSubFases());
      }
    }
  }

  onFaseChange(): void {
    if (this.fase === 'QA' && !this.qaSubFases.length) {
      this.qaSubFases = this.getDefaultQaSubFases();
    } else if (this.fase === 'PRODUCCION' && !this.prodSubFases.length) {
      this.prodSubFases = this.getDefaultProdSubFases();
    }
  }

  getSubFasesActivas(): SubFaseForm[] {
    if (this.fase === 'QA')         return this.qaSubFases;
    if (this.fase === 'PRODUCCION') return this.prodSubFases;
    return [];
  }

  get faseColor(): string {
    const entry = this.FASE_COLORS[this.fase as keyof typeof this.FASE_COLORS];
    return entry?.main ?? this.FASE_COLORS.DESARROLLO.main;
  }

  get faseColorLight(): string {
    const entry = this.FASE_COLORS[this.fase as keyof typeof this.FASE_COLORS];
    return entry?.light ?? this.FASE_COLORS.DESARROLLO.light;
  }

  get barColor(): string {
    return this.faseColor;
  }

  readonly FASES = [
    { value: 'DESARROLLO', label: 'Desarrollo', icon: 'code'          },
    { value: 'QA',         label: 'QA',         icon: 'science'       },
    { value: 'PRODUCCION', label: 'Producción',  icon: 'rocket_launch' },
  ];

  setFase(value: string): void {
    if (value === 'PRODUCCION' && !this.qaSubFasesCompletas) {
      swal.fire({
        title: 'QA incompleto',
        text: 'Para avanzar a Producción debes completar todas las subfases requeridas de QA: Solicitud, Ejecución y VoBo.',
        icon: 'warning',
        buttonsStyling: false,
        customClass: { confirmButton: 'btn btn-warning' },
        confirmButtonText: 'Entendido',
      });
      return;
    }
    this.fase = value;
    this.onFaseChange();
  }

  get qaSubFasesCompletas(): boolean {
    if (!this.qaSubFases.length) return false;
    const requeridas = this.qaSubFases.filter(sf => !sf.opcional);
    return requeridas.length > 0 && requeridas.every(sf => sf.completado);
  }

  toggleSubFase(sf: SubFaseForm): void {
    sf.completado = !sf.completado;
  }

  isFasePasada(faseValue: string): boolean {
    const order = ['DESARROLLO', 'QA', 'PRODUCCION'];
    return order.indexOf(this.fase ?? '') > order.indexOf(faseValue);
  }

  chipStyle(faseValue: string): Record<string, string> {
    const key = faseValue as keyof typeof this.FASE_COLORS;
    const colors = this.FASE_COLORS[key] ?? this.FASE_COLORS.DESARROLLO;
    return { '--chip-color': colors.main, '--chip-light': colors.light };
  }

  calcularAvanceGral(): number {
    if (this.fase === 'QA') {
      const requeridas = this.qaSubFases.filter(sf => !sf.opcional);
      const todasCompletas = requeridas.length > 0 && requeridas.every(sf => sf.completado);
      return todasCompletas ? 95 : 85;
    }
    if (this.fase === 'PRODUCCION') {
      const pesos: Record<string, number> = { 'Instalación': 1, 'Validación': 2, 'VoBo': 2 };
      const puntos = this.prodSubFases
        .filter(sf => sf.completado)
        .reduce((sum, sf) => sum + (pesos[sf.nombre] ?? 0), 0);
      return 95 + puntos; // máx 95 + 5 = 100
    }
    return Math.round((this.avanceReal ?? 0) * 0.85);
  }

  limpiar(): void {
    this.avanceReal   = null;
    this.comentario   = '';
    this.qaSubFases   = this.getDefaultQaSubFases();
    this.prodSubFases = this.getDefaultProdSubFases();
  }

  save(): void {
    if (!this.fase) {
      swal.fire('Atención', 'Debes seleccionar una fase', 'warning');
      return;
    }
    if (!this.comentario?.trim()) {
      swal.fire('Atención', 'Debes escribir un comentario', 'warning');
      return;
    }

    const request = {
      idProyecto:        this.proyecto.idProyecto ?? '',
      avanceSeguimiento: this.fase === 'DESARROLLO' ? (this.avanceReal ?? 0) : 0,
      descripcion:       this.comentario.trim(),
      fecha:             this.formatFecha(new Date()),
      fase:              this.fase,
      qaSubFases:        this.fase === 'QA'         ? this.qaSubFases  : null,
      prodSubFases:      this.fase === 'PRODUCCION' ? this.prodSubFases : null,
    };

    this.isSaving = true;
    this.proyectosService.guardarAvanceProyecto(request).subscribe({
      next: (response) => {
        swal.fire({
          title: 'Éxito',
          text: 'Avance registrado correctamente',
          icon: 'success',
          buttonsStyling: false,
          customClass: { confirmButton: 'btn btn-success' },
          confirmButtonText: 'Aceptar',
        }).then(() => this.dialogRef.close(true));
      },
      error: () => {
        this.isSaving = false;
        swal.fire('Error', 'No se pudo guardar el avance. Intenta de nuevo.', 'error');
      },
    });
  }

  private formatFecha(date: Date): string {
    const m = ('0' + (date.getMonth() + 1)).slice(-2);
    const d = ('0' + date.getDate()).slice(-2);
    return `${date.getFullYear()}-${m}-${d}`;
  }

  get historialAvances(): any[] {
    return [...(this.proyecto.comentariosAvances ?? [])]
      .sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
  }

  getFaseLabel(fase: string): string {
    const f = this.FASES.find(x => x.value === fase);
    return f?.label ?? fase ?? 'Desarrollo';
  }

  getFaseIcon(fase: string): string {
    const f = this.FASES.find(x => x.value === fase);
    return f?.icon ?? 'code';
  }

  getFaseColor(fase: string): string {
    const entry = this.FASE_COLORS[fase as keyof typeof this.FASE_COLORS];
    return entry?.main ?? this.FASE_COLORS.DESARROLLO.main;
  }

  private mapSubFasesFromStored(stored: any[] | null | undefined, defaults: SubFaseForm[]): SubFaseForm[] {
    if (!stored?.length) return defaults;
    return defaults.map(def => {
      const match = stored.find(s => s?.nombre === def.nombre);
      return match ? { ...def, completado: Boolean(match.completado) } : def;
    });
  }

  private getDefaultQaSubFases(): SubFaseForm[] {
    return [
      { nombre: 'Solicitud',  icon: 'description', completado: false },
      { nombre: 'Ejecución',  icon: 'play_arrow',  completado: true },
      { nombre: 'Corrección', icon: 'build',       completado: false, opcional: true },
      { nombre: 'VoBo',       icon: 'verified',    completado: false },
    ];
  }

  private getDefaultProdSubFases(): SubFaseForm[] {
    return [
      { nombre: 'Instalación', icon: 'download',   completado: true  },
      { nombre: 'Validación',  icon: 'fact_check', completado: true  },
      { nombre: 'VoBo',        icon: 'verified',   completado: false },
    ];
  }
}
